import {
  countTrendPointsOutsideLoHi,
  formatOneDecimal,
} from "@/components/root-cause-analysis/rca-measurement-trend-shared.jsx";
import { cn } from "@/lib/utils.js";

const STAT_ROW_CLASS =
  "flex w-full min-w-0 flex-wrap items-baseline content-start gap-x-1.5 gap-y-0.5 text-[10px] leading-tight";

/**
 * @param {{
 *   label: string,
 *   value: string,
 *   labelClassName?: string,
 *   valueClassName?: string,
 *   className?: string,
 * }} props
 */
export function RcaTrendStatItem({
  label,
  value,
  labelClassName,
  valueClassName,
  className,
}) {
  return (
    <span
      role='listitem'
      className={cn(
        "inline-flex shrink-0 items-baseline gap-0.5 whitespace-nowrap",
        className
      )}>
      <span className={cn("text-muted-foreground", labelClassName)}>{label}</span>
      <span
        className={cn(
          "font-semibold tabular-nums text-foreground",
          valueClassName
        )}>
        {value}
      </span>
    </span>
  );
}

/**
 * Priority stats: bold, colored label and value.
 * @param {{ label: string, value: string, tone: "danger" | "warning" }} props
 */
function RcaTrendPriorityStatItem({ label, value, tone }) {
  // danger = anomalies (red). warning = outside Lo/Hi — rose stays in the
  // “problem” family on light and dark without competing with amber chart series
  // or reading as a soft yellow caution.
  const color =
    tone === "danger"
      ? "text-red-600 dark:text-red-400"
      : "text-rose-700 dark:text-rose-400";
  return (
    <RcaTrendStatItem
      label={label}
      value={value}
      labelClassName={cn("font-semibold", color)}
      valueClassName={color}
    />
  );
}

/**
 * @param {{ children: import("react").ReactNode, className?: string }} props
 */
function RcaTrendStatsRow({ children, className }) {
  return (
    <div className={cn(STAT_ROW_CLASS, className)} role='group'>
      {children}
    </div>
  );
}

/**
 * Right-hand statistics for RCA trend cards (Key Sensors, Remaining Sensors).
 *
 * @param {{
 *   row: import("@/components/root-cause-analysis/rca-measurement-trend-shared.jsx").MeasurementWithStatsRow|Record<string, unknown>,
 *   useAlarmLimits?: boolean,
 *   className?: string,
 * }} props
 */
export function RcaTrendStatsPanel({ row, useAlarmLimits = false, className }) {
  const anomalies = Number(row.Anomalies) || 0;
  const outsideLoHiCount = useAlarmLimits
    ? countTrendPointsOutsideLoHi(row)
    : null;

  return (
    <div
      role='list'
      className={cn(
        "flex w-full min-w-0 flex-col gap-0.5 text-[10px] leading-tight",
        className
      )}>
      <RcaTrendStatsRow>
        <RcaTrendPriorityStatItem
          label='anomalies'
          value={String(anomalies)}
          tone='danger'
        />
        {useAlarmLimits && outsideLoHiCount != null ? (
          <RcaTrendPriorityStatItem
            label='outside Lo/Hi'
            value={String(outsideLoHiCount)}
            tone='warning'
          />
        ) : null}
      </RcaTrendStatsRow>
      <RcaTrendStatsRow>
        <RcaTrendStatItem label='Mean' value={formatOneDecimal(row.Avg)} />
        <RcaTrendStatItem label='σ' value={formatOneDecimal(row.StdDev)} />
        <RcaTrendStatItem label='Min' value={formatOneDecimal(row.Min)} />
        <RcaTrendStatItem label='Max' value={formatOneDecimal(row.Max)} />
      </RcaTrendStatsRow>
    </div>
  );
}

/**
 * Statistics strip for pre-shutdown drift summary rows.
 *
 * @param {{
 *   row: import("@/components/root-cause-analysis/rca-measurement-trend-shared.jsx").MeasurementWithStatsRow|Record<string, unknown>,
 *   meanFirst: number,
 *   meanLast: number,
 *   driftLabel?: string,
 *   useAlarmLimits?: boolean,
 *   className?: string,
 * }} props
 */
export function RcaDriftStatsPanel({
  row,
  meanFirst,
  meanLast,
  driftLabel,
  useAlarmLimits = false,
  className,
}) {
  const anomalies = Number(row.Anomalies) || 0;
  const outsideLoHiCount = useAlarmLimits
    ? countTrendPointsOutsideLoHi(row)
    : null;

  return (
    <div
      role='list'
      className={cn(
        "flex w-full min-w-0 flex-col gap-0.5 text-[10px] leading-tight",
        className
      )}>
      <RcaTrendStatsRow>
        <RcaTrendPriorityStatItem
          label='ANOMALIES (vales > |1σ|)'
          value={String(anomalies)}
          tone='danger'
        />
        {useAlarmLimits && outsideLoHiCount != null ? (
          <RcaTrendPriorityStatItem
            label='OUTSIDE LO/HI'
            value={String(outsideLoHiCount)}
            tone='warning'
          />
        ) : null}
        {driftLabel != null ? (
          <RcaTrendStatItem
            label='DRIFT'
            value={driftLabel}
            labelClassName='font-semibold uppercase tracking-wide text-muted-foreground'
            valueClassName='font-semibold text-foreground'
          />
        ) : null}
      </RcaTrendStatsRow>
      <RcaTrendStatsRow>
        <RcaTrendStatItem
          label='1st Window Mean'
          value={formatOneDecimal(meanFirst)}
        />
        <RcaTrendStatItem
          label='2nd Window Mean'
          value={formatOneDecimal(meanLast)}
        />
        <RcaTrendStatItem label='Min' value={formatOneDecimal(row.Min)} />
        <RcaTrendStatItem label='Mean' value={formatOneDecimal(row.Avg)} />
        <RcaTrendStatItem label='Max' value={formatOneDecimal(row.Max)} />
      </RcaTrendStatsRow>
    </div>
  );
}
